// useTrapFocus.js
/** useTrapFocus - A custom hook to trap focus within a modal or popup.
 *
 * @param {object} ref - A React ref pointing to the modal container.
 * @param {boolean} isOpen - A boolean indicating whether the modal is open.
 */

import { useEffect } from "react";

const useTrapFocus = (ref, isOpen) => {
    useEffect(() => {
        if (!isOpen || !ref.current) return; // Only run when modal is open

        const trapFocus = (e) => {
            if (e.key !== "Tab") return;

            const focusableElements = ref.current.querySelectorAll(
                'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
            );

            if (!focusableElements.length) return;

            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            if (e.shiftKey && document.activeElement === firstFocusable) {
                lastFocusable.focus();
                e.preventDefault();
            } else if (!e.shiftKey && document.activeElement === lastFocusable) {
                firstFocusable.focus();
                e.preventDefault();
            }
        };

        document.addEventListener("keydown", trapFocus);
        return () => document.removeEventListener("keydown", trapFocus);
    }, [isOpen, ref]); // Only re-run when isOpen changes
};

export default useTrapFocus;
